import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import Accordion from "@whoosh/design-system/src/components/controls/Accordion";
import { Text, VStack } from "@whoosh/design-system/src/components/primitive";

import { PackageType } from "../../CurrentPackagesModal/components/__generated__/ModalLoaderQuery.graphql";
import { PackageItem } from "./PackageItem";
import { PackagesListEmptyState } from "./PackagesListEmptyState";
import { PackagesList_customer$key } from "./__generated__/PackagesList_customer.graphql";

type Props = {
  type: PackageType;
  customerRef?: PackagesList_customer$key | null;
};

export const PackagesList = ({ type, customerRef }: Props) => {
  const customer = useFragment(PackagesListFragmentString, customerRef);

  const activePackages =
    customer?.activePackages?.edges?.flatMap((e) =>
      e?.node ? [e.node] : []
    ) ?? [];

  const upcomingPackages =
    customer?.upcomingPackages?.edges?.flatMap((e) =>
      e?.node ? [e.node] : []
    ) ?? [];

  const expiredPackages =
    customer?.expiredPackages?.edges?.flatMap((e) =>
      e?.node ? [e.node] : []
    ) ?? [];

  const noActiveOrUpcomingPackages =
    activePackages.length === 0 && upcomingPackages.length === 0;

  return (
    <VStack gap={6} paddingHorizontal={6}>
      {noActiveOrUpcomingPackages ? (
        <PackagesListEmptyState type={type} customerRef={customer} />
      ) : null}
      {activePackages.length > 0 ? (
        <VStack gap={4}>
          <Text variant="mainSmall">{t`Active`}</Text>
          {activePackages.map((pkg) => (
            <PackageItem memberPackageRef={pkg} status="ACTIVE" key={pkg.id} />
          ))}
        </VStack>
      ) : null}
      {upcomingPackages.length > 0 ? (
        <VStack gap={4}>
          <Text variant="mainSmall">{t`Upcoming`}</Text>
          {upcomingPackages.map((pkg) => (
            <PackageItem
              memberPackageRef={pkg}
              status="UPCOMING"
              key={pkg.id}
            />
          ))}
        </VStack>
      ) : null}
      {expiredPackages.length > 0 ? (
        <Accordion
          header={
            <Text variant="mainSmall">{t`Expired (${expiredPackages.length})`}</Text>
          }
          isOpen={true}
        >
          <VStack gap={4} pt={4}>
            {expiredPackages.map((pkg) => (
              <PackageItem
                memberPackageRef={pkg}
                status="EXPIRED"
                key={pkg.id}
              />
            ))}
          </VStack>
        </Accordion>
      ) : null}
    </VStack>
  );
};

const PackagesListFragmentString = graphql`
  fragment PackagesList_customer on Customer {
    ...PackagesListEmptyState_customer
    activePackages: memberPackages(type: $packageType, status: ACTIVE) {
      edges {
        node {
          id
          ...PackageItem_memberPackage
        }
      }
    }
    upcomingPackages: memberPackages(type: $packageType, status: UPCOMING) {
      edges {
        node {
          id
          ...PackageItem_memberPackage
        }
      }
    }
    expiredPackages: memberPackages(type: $packageType, status: EXPIRED) {
      edges {
        node {
          id
          ...PackageItem_memberPackage
        }
      }
    }
  }
`;
